/* eslint-disable react/no-danger */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import Cookies from 'js-cookie';
import { camelCase } from 'lodash';
// import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'wouter';

import useLocationNext from '../../../hooks/useLocationNext';
import * as AppPropTypes from '../../../lib/PropTypes';

import videos from '../../../../videos/index';
import { useContest } from '../../../contexts/ContestContext';
import { useLabels } from '../../../contexts/LabelsContext';
import { useThemeClassName, useTheme } from '../../../contexts/ThemeContext';
import Blocks from '../../blocks/Blocks';
import LoginForm from '../../forms/LoginForm';
import Logo from '../../icons/Logo';
import VideoModal from '../../modals/VideoModal';
import AgeGate from '../../partials/AgeGate';

import styles from '../../../../styles/pages/home-nhl.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
};

const defaultProps = {};

function HomeAllstarPage({ page }) {
    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const next = useLocationNext();

    const currentUser = useUser();
    const [initialUser] = useState(currentUser);
    const hasInitialUser = initialUser !== null;

    useEffect(() => {
        if (hasInitialUser) {
            navigate(route('play'));
        }
    }, [hasInitialUser, navigate, route]);

    const { blocks = [], splashVideo = null } = page || {};

    const topBlock = useMemo(() => (blocks.length > 0 ? [blocks[0]] : []), [blocks]);

    const otherBlocks = useMemo(
        () =>
            (blocks || [])
                .map((item, index) => ({ ...item, index }))
                .filter((item, index) => index > 0),
        [blocks],
    );

    const { tokenModalDescription = null } = useLabels();

    const video = videos.find(({ id: videoId }) => videoId === splashVideo) || null;

    const {
        /* prizes, legal, */ optins,
        ended = false,
        started = false,
        access,
        title,
        startAt,
    } = useContest() || {};
    const {
        ageGate = false,
        disabledProvinces = null,
        profile = null,
        requiresPincode: hasPincodesField = false,
    } = access || {};
    const { redirectOnEnter: redirectToProfile = false } = profile || {};
    const { logo } = useTheme();
    const themeClassName = useThemeClassName(styles);

    const { component: logoComponent } = logo || {};
    const intl = useIntl();
    const startAtDate = useMemo(() => (startAt !== null ? parseISO(startAt) : null), [startAt]);

    const [province, setProvince] = useState(Cookies.get('ag-province') || null);
    const [birthdate, setBirthdate] = useState(Cookies.get('ag-birthdate') || null);
    const [unlocked, setUnlocked] = useState(!ageGate);
    const [showVideo, setShowVideo] = useState(false);
    const [registered, setRegistered] = useState(false);
    const [userProfileCompleted, setUserProfileCompleted] = useState(true);

    const onProvinceChange = useCallback((value) => {
        setProvince(value);
    }, []);

    const onBirthdateChange = useCallback((value) => {
        setBirthdate(value);
    }, []);

    const redirect = useCallback(() => {
        if (!userProfileCompleted || (registered && redirectToProfile)) {
            navigate(route('register'));
        } else if (next !== null && next.length > 0) {
            navigate(next);
        } else {
            navigate(ended ? route('account') : route('play'));
        }
    }, [navigate, route, next, ended, userProfileCompleted, registered]);

    const onLoginSucess = useCallback(
        (user, isRegister) => {
            const { profileCompleted = true } = user;
            setUserProfileCompleted(profileCompleted);
            setRegistered(isRegister);
            if (splashVideo !== null && video !== null && isRegister) {
                setShowVideo(true);
                return;
            }
            redirect();
        },
        [navigate, route, next, ended],
    );

    const onVideoClose = useCallback(() => {
        redirect();
    }, [video]);

    const onAgeGateSuccess = useCallback(({ provinceId, birthdate: date }) => {
        Cookies.set('ag-province', provinceId, { expires: 3600 });
        Cookies.set('ag-birthdate', date, { expires: 3600 });
        setUnlocked(true);
    }, []);

    useEffect(() => {
        if (unlocked) {
            window.scroll({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [unlocked]);

    const loginExtraFields = useMemo(
        () => ({ region: province, birthdate }),
        [province, birthdate],
    );

    const notStarted = !started && !ended;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.unlocked]: unlocked,
                    [styles.withPincode]: hasPincodesField,
                    [styles.centered]: notStarted,
                },
                themeClassName,
            ])}
        >
            {showVideo ? <VideoModal video={video} closesOnEnded onClose={onVideoClose} /> : null}

            {!unlocked ? (
                <div className={styles.welcomeContainer}>
                    <FormattedMessage defaultMessage="Welcome to" description="Welcome text" />
                </div>
            ) : null}
            <div className={styles.logoContainer}>
                <Logo
                    component={logoComponent}
                    className={classNames([
                        styles.logo,
                        {
                            [styles[camelCase(logoComponent)]]: logoComponent !== null,
                        },
                    ])}
                />
            </div>
            {notStarted ? (
                <div className={styles.inner}>
                    <div className={styles.emphasis}>
                        <FormattedMessage
                            defaultMessage="Get ready!{br}{br}{contest} starts{br}{date}."
                            values={{
                                contest: title,
                                date: (
                                    <FormattedDate
                                        value={startAtDate}
                                        day="numeric"
                                        month="long"
                                        year="numeric"
                                    />
                                ),
                            }}
                        />
                    </div>
                </div>
            ) : (
                <div className={styles.inner}>
                    <div className={styles.ageGateContainer}>
                        <AgeGate
                            className={styles.ageGate}
                            provinceId={province}
                            disabledProvinces={disabledProvinces}
                            birthdate={birthdate}
                            onSuccess={onAgeGateSuccess}
                            onProvinceChange={onProvinceChange}
                            onBirthdateChange={onBirthdateChange}
                            submitClassName={styles.ageGateSubmit}
                        />
                    </div>
                    <div className={styles.lockedContent}>
                        <div className={styles.pageContent}>
                            <Blocks
                                blockClassName={styles.block}
                                blockTypeClassNames={styles}
                                blocks={topBlock}
                            />
                        </div>
                        <LoginForm
                            className={styles.login}
                            labelClassName={styles.loginFormLabel}
                            inputClassName={styles.loginInput}
                            submitClassName={styles.loginSubmit}
                            errorsClassName={styles.loginErrors}
                            phoneLabel={intl.formatMessage({
                                defaultMessage: 'Enter your phone number',
                                description: 'NHL login cta label',
                            })}
                            pincodeLabel={intl.formatMessage({
                                defaultMessage:
                                    'Your PIN code can be entered now or later, but is required to play.',
                                description: 'NHL login cta label',
                            })}
                            submitLabel={
                                profile !== null
                                    ? intl.formatMessage({
                                          defaultMessage: 'Next',
                                          description: 'Button label',
                                      })
                                    : intl.formatMessage({
                                          defaultMessage: 'Play now',
                                          description: 'NHL login submit label',
                                      })
                            }
                            tokenModalTitle={intl.formatMessage({
                                defaultMessage: 'Are you{br}ready?',
                                description: 'NHL login token modal title',
                            })}
                            tokenModalDescription={
                                tokenModalDescription ??
                                intl.formatMessage({
                                    defaultMessage:
                                        'We’ll send an activation code to your phone shortly. Once received, please enter the code here to proceed.',
                                    description: 'NHL login token modal title',
                                })
                            }
                            withPincode={hasPincodesField}
                            optins={optins}
                            extraFields={loginExtraFields}
                            onSuccess={onLoginSucess}
                        />
                        <div className={styles.pageContent}>
                            <Blocks
                                blockClassName={styles.block}
                                blockTypeClassNames={styles}
                                blocks={otherBlocks}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

HomeAllstarPage.propTypes = propTypes;
HomeAllstarPage.defaultProps = defaultProps;

export default HomeAllstarPage;
